import React, { useState } from 'react';
import Spinner from '@/components/UI/Spinner';
import classNames from 'classnames';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

type PaginatedTableProps = {
  data?: Record<string, any>[];
  rowsPerPage?: number;
  headerCells: string[];
  emptyDataMessage: string;
  loading?: boolean;
};

const PaginatedTable: React.FC<PaginatedTableProps> = ({
  data,
  headerCells,
  rowsPerPage = 5,
  loading,
  emptyDataMessage,
}) => {
  const [currentPage, setCurrentPage] = useState(0);

  const totalPages = Math.ceil((data?.length ?? 0) / rowsPerPage);
  const start = currentPage * rowsPerPage;
  const paginatedData = data?.slice(start, start + rowsPerPage);
  const showTableData = paginatedData && paginatedData.length > 0 && !loading;
  const showPagination = showTableData && totalPages > 1;
  const noDataPresent = paginatedData && paginatedData.length < 1 && !loading;

  return (
    <>
      <table className="w-full">
        <thead>
          <tr>
            {headerCells.map((col, index) => {
              const isLast = index === headerCells.length - 1;
              const isFirst = index === 0;
              return (
                <th
                  className={classNames(
                    'bg-darkGray text-white text-[14px] md:text-[16px] p-[12px] md:p-[16px] shadow-md ',
                    {
                      'text-left rounded-tl-xl': isFirst,
                      'text-right rounded-tr-xl': isLast,
                      'text-center': !isFirst && !isLast,
                    },
                  )}
                  key={col}
                >
                  {col}
                </th>
              );
            })}
          </tr>
        </thead>
        {showTableData && (
          <tbody className="w-full text-[12px] md:text-[16px] shadow-md rounded-b-xl overflow-hidden">
            {paginatedData.map((row, rowIndex) => {
              const isOdd = rowIndex % 2 !== 0;
              const isLastRow =
                rowIndex === rowsPerPage - 1 ||
                rowIndex === (paginatedData.length % rowsPerPage) - 1;
              const rowColumns = Object.keys(row);

              return (
                <tr key={rowIndex}>
                  {rowColumns.map((col, colIndex) => {
                    const isLast = colIndex === rowColumns.length - 1;
                    const isFirst = colIndex === 0;
                    return (
                      <td
                        className={classNames('p-[12px] md:p-[16px]', {
                          'bg-gray-200': isOdd,
                          'bg-white': !isOdd,
                          'text-left': isFirst,
                          'text-right': isLast,
                          'text-center': !isFirst && !isLast,
                          'rounded-bl-xl': isLastRow && isFirst,
                          'rounded-br-xl': isLastRow && isLast,
                        })}
                        key={col}
                      >
                        {row[col]}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        )}
      </table>
      {(noDataPresent || loading) && (
        <div className="w-full text-center text-[12px] md:text-[16px] py-[24px] md:py-[32px] rounded-b-xl bg-white shadow-md">
          {noDataPresent && emptyDataMessage}
          {loading && <Spinner />}
        </div>
      )}

      {showPagination && (
        <div className="mt-4 flex justify-center items-center gap-x-8 text-sm">
          <button
            className={classNames({
              invisible: currentPage === 0,
            })}
            type="button"
            onClick={() => setCurrentPage(p => Math.max(p - 1, 0))}
            disabled={currentPage === 0}
          >
            <ChevronLeftIcon width={30} height={30} />
          </button>
          <span>
            {currentPage + 1} / {totalPages}
          </span>
          <button
            className={classNames({
              invisible: currentPage === totalPages - 1,
            })}
            type="button"
            onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages - 1))}
            disabled={currentPage >= totalPages - 1}
          >
            <ChevronRightIcon width={30} height={30} />
          </button>
        </div>
      )}
    </>
  );
};

export default PaginatedTable;
